import { useState, useEffect, useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import Input from "./Input";
import TextAnimH1 from "./TextAnimH1";
import Button from "./Button";

// Custom Select Component
const CustomSelect = ({
  name,
  value,
  onChange,
  options,
  required,
  hasError,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option, index) => {
    // Don't allow selection of the first option (disabled placeholder)
    if (index === 0) return;

    const selectedValue = option;
    onChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
  };

  const displayValue = value || options[0]; // Always show first option as placeholder

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-transparent border-0 pointer-events-auto border-b pb-8 py-2 text-2xl font-semibold focus:outline-none transition-colors cursor-pointer flex justify-between items-center ${
          hasError
            ? "border-b-2 border-red-500"
            : "border-b border-zinc-600 hover:border-zinc-400 focus:border-zinc-400"
        } ${
          value
            ? "text-zinc-800 dark:text-zinc-200"
            : "text-zinc-500 dark:text-zinc-400"
        }`}
      >
        <span>{displayValue}</span>
        <svg
          className={`w-5 h-5 transition-transform duration-200 text-zinc-600 dark:text-zinc-400 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-600 rounded-lg shadow-lg dark:shadow-zinc-900/50 overflow-hidden">
          {options.map((option, index) => (
            <div
              key={index}
              onClick={() => handleSelect(option, index)}
              className={`px-4 py-3 text-lg font-medium transition-colors ${
                index === 0
                  ? "text-zinc-400 dark:text-zinc-500  cursor-not-allowed bg-zinc-50 dark:bg-zinc-700/50"
                  : `cursor-pointer dark:text-white hover:bg-[#EE005B]/20  ${
                      value === option ? "bg-[#EE005B] text-white" : ""
                    }`
              }`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const FormField = ({
  field,
  value,
  onChange,
  isLanding = false,
  delay = 0,
  errors = {},
  isPopup = false,
}) => {
  const { id, label, name, type, placeholder, required, isTextarea, options } =
    field;
  const hasError = errors[name];

  return (
    <div>
      <label className="block text-sm dark:text-zinc-200 mb-1">
        {id} {label}
      </label>
      {isTextarea ? (
        <textarea
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className={`w-full bg-transparent border-0 pointer-events-auto py-2 text-2xl dark:placeholder:text-zinc-800 placeholder:text-zinc-300 font-semibold focus:outline-none focus:border-zinc-400 transition-colors resize-none ${
            hasError ? "border-b-2 border-red-500" : ""
          }`}
          rows="2"
          required={required}
        />
      ) : type === "select" ? (
        <CustomSelect
          name={name}
          value={value}
          onChange={onChange}
          options={options}
          required={required}
          hasError={hasError}
          placeholder={placeholder}
        />
      ) : (
        <Input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          hasError={hasError}
        />
      )}
      {hasError && <p className="text-red-500 text-sm mt-1">{errors[name]}</p>}
    </div>
  );
};

export default function ContactForm() {
  // Tab switching state
  const [activeTab, setActiveTab] = useState("dominance");
  const tabRef = useRef([]);
  const indicatorRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    services: "",
    message: "",
    // Additional fields for different tabs
    Full_Name: "",
    Contact_Number: "",
    Additional_Info: "",
    First_Name: "",
    Last_Name: "",
    Purpose: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Handle Tab Switching Animation
  useEffect(() => {
    const activeIndex = ["dominance", "help", "pack"].indexOf(activeTab);
    if (tabRef.current[activeIndex] && indicatorRef.current) {
      const { offsetLeft, offsetWidth } = tabRef.current[activeIndex];
      gsap.to(indicatorRef.current, {
        x: offsetLeft,
        width: offsetWidth,
        duration: 0.1,
        ease: "power1.out",
      });
    }
  }, [activeTab]);

  // Form validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Check if phone starts with country code (+ followed by digits)
    const phoneRegex = /^\+\d{1,4}\s?\d{4,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  const validateForm = () => {
    const newErrors = {};

    // Tab-specific validation
    if (activeTab === "dominance") {
      if (!form.Full_Name?.trim()) {
        newErrors.Full_Name = "Full Name is required";
      }
      if (!form.Contact_Number?.trim()) {
        newErrors.Contact_Number = "Contact Number is required";
      }
      if (!form.email?.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(form.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!form.Additional_Info?.trim()) {
        newErrors.Additional_Info = "Additional Information is required";
      }
    } else if (activeTab === "help") {
      if (!form.Full_Name?.trim()) {
        newErrors.Full_Name = "Full Name is required";
      }
      if (!form.phone?.trim()) {
        newErrors.phone = "Phone is required";
      }
      if (!form.email?.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(form.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!form.message?.trim()) {
        newErrors.message = "Message is required";
      }
    } else if (activeTab === "pack") {
      if (!form.First_Name?.trim()) {
        newErrors.First_Name = "First Name is required";
      }
      if (!form.Last_Name?.trim()) {
        newErrors.Last_Name = "Last Name is required";
      }
      if (!form.phone?.trim()) {
        newErrors.phone = "Phone is required";
      }
      if (!form.email?.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(form.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!form.message?.trim()) {
        newErrors.message = "Message is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form fields configuration for different tabs
  const getFormFields = () => {
    switch (activeTab) {
      case "dominance":
        return [
          {
            id: "01",
            label: "What's your full name?",
            name: "Full_Name",
            type: "text",
            placeholder: "Full Name*",
            required: true,
          },
          {
            id: "02",
            label: "What's your contact number?",
            name: "Contact_Number",
            type: "text",
            placeholder: "Contact Number*",
            required: true,
          },
          {
            id: "03",
            label: "What's your email?",
            name: "email",
            type: "email",
            placeholder: "Email Address*",
            required: true,
          },
          {
            id: "04",
            label: "What's the purpose?",
            name: "Purpose",
            type: "select",
            placeholder: "Purpose",
            required: false,
            options: ["Purpose", "Business Inquiry", "Collaboration"],
          },
          {
            id: "05",
            label: "Additional Information",
            name: "Additional_Info",
            type: "text",
            placeholder: "Additional Information*",
            required: true,
            isTextarea: true,
          },
        ];
      case "help":
        return [
          {
            id: "01",
            label: "What's your full name?",
            name: "Full_Name",
            type: "text",
            placeholder: "Full Name*",
            required: true,
          },
          {
            id: "02",
            label: "What's your phone number?",
            name: "phone",
            type: "text",
            placeholder: "Phone*",
            required: true,
          },
          {
            id: "03",
            label: "What's your email?",
            name: "email",
            type: "email",
            placeholder: "Email*",
            required: true,
          },
          {
            id: "04",
            label: "Your Message",
            name: "message",
            type: "text",
            placeholder: "Message*",
            required: true,
            isTextarea: true,
          },
        ];
      case "pack":
        return [
          {
            id: "01",
            label: "What's your first name?",
            name: "First_Name",
            type: "text",
            placeholder: "First Name*",
            required: true,
          },
          {
            id: "02",
            label: "What's your last name?",
            name: "Last_Name",
            type: "text",
            placeholder: "Last Name*",
            required: true,
          },
          {
            id: "03",
            label: "What's your phone number?",
            name: "phone",
            type: "text",
            placeholder: "Phone*",
            required: true,
          },
          {
            id: "04",
            label: "What's your email?",
            name: "email",
            type: "email",
            placeholder: "Email*",
            required: true,
          },
          {
            id: "05",
            label: "Your Message",
            name: "message",
            type: "text",
            placeholder: "Message*",
            required: true,
            isTextarea: true,
          },
        ];
      default:
        return [];
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      // Include tab information in submission
      const submissionData = {
        ...form,
        tabType: activeTab,
        submittedAt: new Date().toISOString(),
      };

      // Send to serverless endpoint - map fields to backend shape
      const endpoint = "https://faas-server-itun.onrender.com/form-submit";

      const nameVal = (form.Full_Name && form.Full_Name.trim()) ||
        ((form.First_Name || "").trim() || "") + (form.Last_Name ? ` ${form.Last_Name.trim()}` : "") ||
        form.name || "";

      const contactNumber = form.Contact_Number || form.phone || "";
      const mobileNumber = form.phone || form.Contact_Number || "";
      const companyName = form.organization || "";
      const city = "";
      const email = form.email || "";
      const servicesInterested = form.services || form.Purpose || "";
      const messageVal = form.message || form.Additional_Info || "";

      const payload = {
        name: nameVal,
        contactNumber,
        mobileNumber,
        companyName,
        city,
        email,
        servicesInterested,
        message: messageVal,
        sourceWebsite: 'digicots',
      };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Request failed: ${res.status} ${text}`);
      }

      let data = null;
      try { data = await res.json(); } catch (e) { data = null; }

      console.log("Form submitted:", payload, data);
      setSubmitMessage((data && data.message) || "Thank you! Your message has been sent successfully.");

      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        organization: "",
        services: "",
        message: "",
        Full_Name: "",
        Contact_Number: "",
        Additional_Info: "",
        First_Name: "",
        Last_Name: "",
        Purpose: "",
      });
    } catch (error) {
      console.error('ContactForm submit error:', error);
      setSubmitMessage("Sorry, there was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen dark:text-zinc-200">
      {/* Hero Image Section */}
      <div className="relative h-[70vh] hidden xl:block">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/new-final-data/contact/Contact.webp')`,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="xl:px-35 px-10 py-16">
        <div className="mx-auto">
          {/* Header */}
          <div className="mb-16">
            <p className="dark:text-zinc-500 text-md mb-4">Contact us</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <h1 className="xl:text-7xl text-4xl lg:text-8xl font-bold">
                Let's Talk
              </h1>
              <p className="dark:text-zinc-400  text-zinc-700 text-md">
                We know you have project in your mind!
              </p>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left Side - Contact Info */}
            <div className="xl:space-y-12 ">
              {/* Contact Info */}
              <div>
                <p className="text-zinc-500 text-sm mb-4">
                  Don’t sit back <br />
                  Contact us todayeyyey!
                </p>
                <p className="xl:text-3xl text-xl font-semibold mb-2">
                  dominance@digicots.com
                </p>
                <p className="xl:text-3xl text-xl font-semibold">
                  +91 8630116684 | +918826288647
                </p>
              </div>

              {/* Address */}
              <div>
                <p className="text-zinc-500 text-sm mb-4 mt-10">USA Address</p>
                <div className="text-md leading-[1.4]">
                  3422 Old Capitol <br /> Trail Wilmington DE <br /> 19808 , USA
                </div>
              </div>
              {/* Address */}
              <div>
                <p className="text-zinc-500 text-sm mb-4 mt-10">
                  Indian Address
                </p>
                <div className="text-md leading-[1.4]">
                  <p>B 73, Block B,</p>
                  <p>Sector 57, Noida,</p>
                  <p>Uttar Pradesh 201309,</p>
                  <p>India</p>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-zinc-500 text-sm mb-4 mt-10">Social</p>
                <div className="space-y-1">
                  {[
                    { label: "Blogs", href: "/blogs" },
                    {
                      label: "Linkedin",
                      href: "https://www.linkedin.com/company/digicots-interactive-private-limited/",
                    },
                    {
                      label: "Instagram",
                      href: "https://www.instagram.com/digicots_/",
                    },
                  ].map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : "_self"}
                      rel={
                        item.href.startsWith("http")
                          ? "noopener noreferrer"
                          : ""
                      }
                      className="block text-md font-semibold dark:hover:text-zinc-300 hover:text-zinc-500 cursor-pointer transition-colors pointer-events-auto"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              {/* Tab Switching */}
              <div className="mb-10">
                <div className="relative flex justify-center md:justify-between mt-4 rounded-md text-sm sm:text-base space-x-4">
                  {/* Indicator */}
                  <div
                    ref={indicatorRef}
                    className="absolute left-0 bottom-0 h-[1px] bg-[#EE005B] rounded-md transition-all duration-300"
                  ></div>

                  {["dominance", "help", "pack"].map((tab, index) => (
                    <button
                      key={tab}
                      ref={(el) => (tabRef.current[index] = el)}
                      onClick={() => setActiveTab(tab)}
                      type="button"
                      className={`relative py-2 cursor-pointer rounded-md font-semibold md:text-xl  z-10 transition-colors ${
                        activeTab === tab
                          ? "text-black dark:text-white"
                          : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                      }`}
                    >
                      {tab === "dominance"
                        ? "Let's Talk Dominance!"
                        : tab === "help"
                        ? "Howl for Help"
                        : "Join the Pack!"}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {getFormFields().map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={form[field.name]}
                    onChange={handleChange}
                    errors={errors}
                  />
                ))}

                {submitMessage && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitMessage.includes("error")
                        ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                        : "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                    }`}
                  >
                    {submitMessage}
                  </div>
                )}

                <div className="flex items-center justify-between gap-4">
                  <hr className="h-1 xl:w-[60%] hidden md:block md:w-0 border-zinc-600" />
                  <Button disabled={isSubmitting}>
                    {isSubmitting
                      ? "Sending..."
                      : activeTab === "dominance"
                      ? "Let's talk about dominance"
                      : activeTab === "help"
                      ? "Request Help"
                      : "Join the Pack"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
